<template>
  <div class="breakdown-card">
    <div class="w-full px-4 pt-2.5 pb-4 flex items-center justify-between">
      <BaseText type="label" size="sm" class="text-text-normal">
        Country Breakdown
      </BaseText>
      <GraphMetricSelector
        :selected-metric="selectedMetric"
        side="right"
        :shadow="false"
        :disable-selector="isDataPublic"
        @changeMetric="handleMetricChange"
      />
    </div>
    <div class="relative w-full flex pl-4 pr-7 pb-5">
      <transition>
        <div v-if="loading" class="absolute w-full h-full flex items-center justify-center z-50">
          <BaseLoadingSpinnerCircle class="mb-10 text-icon-normal" :width="24" :height="24" :duration="1" />
        </div>
      </transition>
      <!-- Country Names/Flags -->
      <div class="flex flex-col gap-3 pr-5 flex-shrink-0 transition-opacity" :class="{ 'opacity-50': loading }">
        <div v-for="country in countries" :key="`label-${country.name}`" class="flex items-center gap-2">
          <img v-if="country.flag" :src="country.flag" :alt="country.name" class="w-4 h-4 rounded-full" />
          <div v-else class="w-4 h-4 rounded-full bg-neutral-25" />
          <BaseText type="body" size="xs" class="text-text-normal">
            {{ country.name }}
          </BaseText>
        </div>
      </div>
      <!-- Graph Area / X-axis labels -->
      <div ref="graphArea" class="flex-grow h-full flex flex-col gap-3 transition-opacity" :class="{ 'opacity-50': loading }">
        <div v-for="value in dataValues" :key="`bar-${value.country}`" class="relative w-full h-4 flex items-center">
          <div class="w-full h-0 border-t border-border-normal" />
          <div class="graph-bar" :style="{ '--bar-width': `${value.width}px` }" />
        </div>
        <!-- Axis Labels -->
        <div class="w-full flex items-center justify-between">
          <div v-for="label in axisLabels" :key="`country-label-${label}`" class="relative w-0 h-4">
            <BaseText type="body" size="xs" class="absolute left-1/2 transform -translate-x-1/2 text-text-subdued">
              {{ label }}
            </BaseText>
          </div>
        </div>
      </div>
    </div>
    <div v-if="displayShowMore" class="w-full px-4 pb-3">
      <button class="px-2.5 py-1.5 rounded-md transiiton-colors duration-100 hover:bg-neutral-25"
      @click="showMoreCountries = true">
        <BaseText type="label" size="sm" class="text-text-muted">
          Show more
        </BaseText>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { scaleLinear } from 'd3-scale'
import { getCountryName, getCountrySquareFlag } from '@/utils/countryUtils'
import formatLabelString from '@/utils/lens/formatGraphLabelString'
import GraphMetricSelector from './graphs/GraphMetricSelector.vue'

const MAX_AXIS_LABELS = 8

export default {
  name: 'CountryBreakdown',
  components: {
    GraphMetricSelector
  },
  props: {
    selectedMetric: {
      type: String,
      required: true
    },
    dataGroups: {
      type: Array,
      required: true
    },
    currencyRegion: {
      type: String,
      default: 'USD'
    },
    loading: {
      type: Boolean,
      default: false
    },
    isDataPublic: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      axisScale: null,
      axisLabels: [],
      graphAreaWidth: 0,
      resizeObserver: null,
      showMoreCountries: false
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.graphAreaWidth = this.$refs.graphArea.clientWidth
      this.initializeAxisInformation()
      this.resizeObserver = new ResizeObserver(this.handleResize)
      this.resizeObserver.observe(this.$refs.graphArea)
    })
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },
  watch: {
    dataGroups () {
      this.initializeAxisInformation()
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup']),
    truncatedData () {
      return this.dataGroups?.filter(group => group.country !== 'unknown')?.slice(0, this.showMoreCountries ? undefined : 5) ?? []
    },
    countries () {
      return this.truncatedData.map(group => ({
        name: getCountryName(group.country),
        flag: getCountrySquareFlag(group.country)
      }))
    },
    dataValues () {
      if (!this.axisScale) return []
      return this.truncatedData.map(group => ({
        country: group.country,
        width: this.axisScale(group.value)
      }))
    },
    displayShowMore () {
      return this.dataGroups?.length > 5 && !this.showMoreCountries
    }
  },
  methods: {
    initializeAxisInformation () {
      if (!this.dataGroups.length || !this.selectedMetric) {
        this.axisScale = null
        return
      }
      this.computeAxisScales()
    },
    computeAxisScales () {
      const values = this.dataGroups.map(group => group.value)
      let maxValue = Math.max(...values)
      if (maxValue === 0) maxValue = 1
      this.axisScale = scaleLinear()
        .domain([0, maxValue])
        .range([0, this.graphAreaWidth])
        .nice()

      // Compute the axis labels
      let ticks = this.axisScale.ticks(MAX_AXIS_LABELS)
      const reduceTicks = (ticks) => {
        // Ensures that the number of ticks is less than or equal to MAX_AXIS_LABELS
        let tickValues = [...ticks]
        if (ticks.length % 2 === 0) {
          const newMax = ticks[ticks.length - 1] + ticks[1]
          tickValues.push(newMax)
          this.axisScale.domain([0, newMax])
        }
        while (tickValues.length > MAX_AXIS_LABELS) {
          tickValues = tickValues.filter((_, index) => index % 2 === 0)
        }
        return tickValues
      }
      ticks = reduceTicks(ticks)
      this.axisLabels = ticks.map(tick => this.formatAxisLabelString(tick))
    },
    handleMetricChange (key) {
      this.$emit('changeMetric', key)
    },
    handleResize () {
      this.graphAreaWidth = this.$refs.graphArea.clientWidth
      this.initializeAxisInformation()
    },
    formatAxisLabelString (value) {
      const type = this.getMetricLookup?.[this.selectedMetric]?.type
      return formatLabelString(value, type, this.currencyRegion)
    }
  }
}
</script>

<style scoped>
.graph-bar {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: var(--bar-width);
  height: 8px;
  background-color: #5C667A;
  border-radius: 3px;
  z-index: 5;
  animation: barEnter 0.75s ease-out forwards;
}
@keyframes barEnter {
  from { width: 0px; }
  to { width: var(--bar-width); }
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>